export type Language = 'en' | 'es';

export interface ContentType {
  nav: {
    home: string;
    services: string;
    portfolio: string;
    about: string;
    contact: string;
  };
  hero: {
    headline: string;
    tagline: string;
    ctaPortfolio: string;
    ctaBook: string;
    ctaQuote: string;
  };
  expertise: {
    headline: string;
    categories: string[];
  };
  services: {
    headline: string;
    items: { title: string; description: string }[];
  };
  about: {
    headline: string;
    description: string;
    team: string[];
    mission: string;
  };
  techTrust: {
    headline: string;
    equipment: string[];
    content: string[];
    legal: string[];
  };
  portfolio: {
    headline: string;
    categories: string[];
  };
  beforeAfter: {
    headline: string;
    description: string;
  };
  stats: {
    headline: string;
    items: { value: string; label: string }[];
  };
  workflow: {
    headline: string;
    steps: string[];
  };
  testimonials: {
    headline: string;
  };
  contact: {
    headline: string;
    whatsapp: string;
    email: string;
    form: {
      name: string;
      email: string;
      message: string;
      submit: string;
    };
  };
  footer: {
    brand: string;
    tagline: string;
    location: string;
  };
}

export const content: Record<Language, ContentType> = {
  en: {
    nav: {
      home: 'Home',
      services: 'Services',
      portfolio: 'Portfolio',
      about: 'About',
      contact: 'Contact',
    },
    hero: {
      headline: 'VILARIAS',
      tagline: 'PREMIUM VISUALS. REFINED SERVICE. COMPLETE TRUST.',
      ctaPortfolio: 'View Portfolio',
      ctaBook: 'Book a Shoot',
      ctaQuote: 'Request a Quote',
    },
    expertise: {
      headline: 'Visual Media for Premium Properties',
      categories: [
        'Real Estate',
        'Hospitality',
        'Luxury Villas',
        'Hotels',
        'Restaurants',
        'Yachts',
        'Private Events',
        'Construction',
        'Land Investment',
        'Architecture',
        'Developers',
        'Real Estate Agencies',
      ],
    },
    services: {
      headline: 'Our Services',
      items: [
        { title: 'Professional Photography', description: 'High-end property photography that captures every detail with precision and artistry.' },
        { title: 'Professional Videography', description: 'Cinematic video production showcasing properties in their full glory.' },
        { title: 'Drone Cinematography', description: 'Stunning aerial perspectives that reveal the true scale and beauty of every location.' },
        { title: '360° Virtual Tours', description: 'Immersive virtual experiences allowing clients to explore properties from anywhere.' },
        { title: 'Property Management', description: 'Comprehensive visual documentation and presentation services for property professionals.' },
      ],
    },
    about: {
      headline: 'About Vilarias',
      description: 'Vilarias is a Tenerife-based startup founded by professionals from the film and television industry.',
      team: [
        'Film cinematographers',
        'Professional photographers',
        'Drone pilots',
        'FPV operators',
        'Editors',
        'Colorists',
        'Makeup artists',
        'Post-production specialists',
        'Social Media managers',
        'AI artists',
        'Virtual Tour specialists',
      ],
      mission: 'Our mission is to create premium visual experiences that increase property value and help clients sell faster.',
    },
    techTrust: {
      headline: 'Technology & Trust',
      equipment: [
        'DJI Avata 2',
        'DJI Neo',
        'Insta360 X5 (8K)',
        'iPhone 17 Pro Max (4K ProRes)',
        'Professional wireless microphones',
        'Professional lighting',
        'FPV systems',
        'ND filters',
      ],
      content: [
        'Instagram Reels',
        'TikTok',
        'YouTube Shorts',
        'AI enhancement',
        'Virtual staging',
        'Color grading',
        'Before & After',
        'Property presentation',
      ],
      legal: [
        'Fully licensed drone operations',
        'Official Factura invoices',
        'Legal registered business',
        'Insured operations',
        'Propeller guards for safe flights',
        'Professional workflow',
        'Maximum safety',
      ],
    },
    portfolio: {
      headline: 'Portfolio',
      categories: [
        'Luxury Villas',
        'Apartments',
        'Hotels',
        'Restaurants',
        'Construction',
        'Land',
        'Drone',
        'Virtual Tours',
        'AI Projects',
      ],
    },
    beforeAfter: {
      headline: 'Transformation',
      description: 'From empty land to architectural AI visualization — see the power of premium visual storytelling.',
    },
    stats: {
      headline: 'Why Vilarias',
      items: [
        { value: '500+', label: 'Projects' },
        { value: '100+', label: 'Satisfied Clients' },
        { value: '48h', label: 'Average Delivery' },
        { value: '5★', label: 'Client Rating' },
      ],
    },
    workflow: {
      headline: 'Our Workflow',
      steps: [
        'Consultation',
        'Planning',
        'Capture',
        'Editing',
        'AI Enhancement',
        'Delivery',
      ],
    },
    testimonials: {
      headline: 'Client Testimonials',
    },
    contact: {
      headline: "Let's Create Something Exceptional",
      whatsapp: '+34 602 555 188',
      email: 'vilarias360@gmail.com',
      form: {
        name: 'Your Name',
        email: 'Your Email',
        message: 'Your Message',
        submit: 'Send Message',
      },
    },
    footer: {
      brand: 'Vilarias',
      tagline: 'Premium Visual Media',
      location: 'Made in Tenerife',
    },
  },
  es: {
    nav: {
      home: 'Inicio',
      services: 'Servicios',
      portfolio: 'Portafolio',
      about: 'Nosotros',
      contact: 'Contacto',
    },
    hero: {
      headline: 'VILARIAS',
      tagline: 'VISUALES PREMIUM. SERVICIO REFINADO. TOTAL CONFIANZA.',
      ctaPortfolio: 'Ver Portafolio',
      ctaBook: 'Reservar Sesión',
      ctaQuote: 'Solicitar Presupuesto',
    },
    expertise: {
      headline: 'Medios Visuales para Propiedades Premium',
      categories: [
        'Bienes Raíces',
        'Hostelería',
        'Villas de Lujo',
        'Hoteles',
        'Restaurantes',
        'Yates',
        'Eventos Privados',
        'Construcción',
        'Inversión en Terrenos',
        'Arquitectura',
        'Promotores',
        'Agencias Inmobiliarias',
      ],
    },
    services: {
      headline: 'Nuestros Servicios',
      items: [
        { title: 'Fotografía Profesional', description: 'Fotografía de propiedades de alta gama que captura cada detalle con precisión y arte.' },
        { title: 'Videografía Profesional', description: 'Producción de video cinematográfico que muestra las propiedades en todo su esplendor.' },
        { title: 'Cinematografía con Dron', description: 'Perspectivas aéreas impresionantes que revelan la verdadera escala y belleza de cada ubicación.' },
        { title: 'Tours Virtuales 360°', description: 'Experiencias virtuales inmersivas que permiten a los clientes explorar propiedades desde cualquier lugar.' },
        { title: 'Gestión de Propiedades', description: 'Servicios integrales de documentación visual y presentación para profesionales inmobiliarios.' },
      ],
    },
    about: {
      headline: 'Sobre Vilarias',
      description: 'Vilarias es una startup con sede en Tenerife fundada por profesionales de la industria del cine y la televisión.',
      team: [
        'Cinematógrafos de cine',
        'Fotógrafos profesionales',
        'Pilotos de dron',
        'Operadores FPV',
        'Editores',
        'Coloristas',
        'Maquilladores',
        'Especialistas en postproducción',
        'Gestores de redes sociales',
        'Artistas de IA',
        'Especialistas en tours virtuales',
      ],
      mission: 'Nuestra misión es crear experiencias visuales premium que aumenten el valor de la propiedad y ayuden a los clientes a vender más rápido.',
    },
    techTrust: {
      headline: 'Tecnología y Confianza',
      equipment: [
        'DJI Avata 2',
        'DJI Neo',
        'Insta360 X5 (8K)',
        'iPhone 17 Pro Max (4K ProRes)',
        'Micrófonos inalámbricos profesionales',
        'Iluminación profesional',
        'Sistemas FPV',
        'Filtros ND',
      ],
      content: [
        'Instagram Reels',
        'TikTok',
        'YouTube Shorts',
        'Mejora con IA',
        'Escenificación virtual',
        'Gradación de color',
        'Antes y Después',
        'Presentación de propiedades',
      ],
      legal: [
        'Operaciones de dron totalmente licenciadas',
        'Facturas oficiales',
        'Negocio registrado legalmente',
        'Operaciones aseguradas',
        'Protectores de hélice para vuelos seguros',
        'Flujo de trabajo profesional',
        'Máxima seguridad',
      ],
    },
    portfolio: {
      headline: 'Portafolio',
      categories: [
        'Villas de Lujo',
        'Apartamentos',
        'Hoteles',
        'Restaurantes',
        'Construcción',
        'Terrenos',
        'Dron',
        'Tours Virtuales',
        'Proyectos de IA',
      ],
    },
    beforeAfter: {
      headline: 'Transformación',
      description: 'De terreno vacío a visualización arquitectónica con IA — descubra el poder de la narrativa visual premium.',
    },
    stats: {
      headline: 'Por Qué Vilarias',
      items: [
        { value: '500+', label: 'Proyectos' },
        { value: '100+', label: 'Clientes Satisfechos' },
        { value: '48h', label: 'Entrega Promedio' },
        { value: '5★', label: 'Valoración de Clientes' },
      ],
    },
    workflow: {
      headline: 'Nuestro Proceso',
      steps: [
        'Consulta',
        'Planificación',
        'Captura',
        'Edición',
        'Mejora con IA',
        'Entrega',
      ],
    },
    testimonials: {
      headline: 'Testimonios de Clientes',
    },
    contact: {
      headline: 'Creemos Algo Excepcional',
      whatsapp: '+34 602 555 188',
      email: 'vilarias360@gmail.com',
      form: {
        name: 'Su Nombre',
        email: 'Su Email',
        message: 'Su Mensaje',
        submit: 'Enviar Mensaje',
      },
    },
    footer: {
      brand: 'Vilarias',
      tagline: 'Medios Visuales Premium',
      location: 'Hecho en Tenerife',
    },
  },
};

export const testimonialsData: Record<Language, { name: string; role: string; text: string }[]> = {
  en: [
    { name: 'Marco B.', role: 'Villa Owner', text: 'Vilarias transformed how we present our luxury villa. The visuals are breathtaking and bookings increased immediately.' },
    { name: 'Isabella R.', role: 'Hotel Manager', text: 'The team understood our brand perfectly. The photography and video content exceeded all expectations.' },
    { name: 'James T.', role: 'Real Estate Agency', text: 'Professional, fast, and incredibly talented. Our property listings have never looked better.' },
    { name: 'Sophia L.', role: 'Developer', text: 'The drone footage and 360 tours gave our development project a competitive edge. Exceptional quality.' },
    { name: 'Alexandre K.', role: 'International Investor', text: 'I can evaluate properties remotely thanks to Vilarias immersive virtual tours. Game-changing service.' },
  ],
  es: [
    { name: 'Marco B.', role: 'Propietario de Villa', text: 'Vilarias transformó la forma en que presentamos nuestra villa de lujo. Las imágenes son impresionantes y las reservas aumentaron inmediatamente.' },
    { name: 'Isabella R.', role: 'Gerente de Hotel', text: 'El equipo entendió perfectamente nuestra marca. La fotografía y el contenido de video superaron todas las expectativas.' },
    { name: 'James T.', role: 'Agencia Inmobiliaria', text: 'Profesionales, rápidos e increíblemente talentosos. Nuestros listados de propiedades nunca se han visto mejor.' },
    { name: 'Sophia L.', role: 'Promotora', text: 'Las imágenes de dron y los tours 360 dieron a nuestro proyecto de desarrollo una ventaja competitiva. Calidad excepcional.' },
    { name: 'Alexandre K.', role: 'Inversor Internacional', text: 'Puedo evaluar propiedades remotamente gracias a los tours virtuales inmersivos de Vilarias. Un servicio revolucionario.' },
  ],
};
